import { type FC, useEffect, useRef, useState } from "react";

interface MermaidRendererProps {
  content: string;
}

/**
 * Renders Mermaid diagrams. The `mermaid` library is loaded lazily
 * to avoid bloating the initial bundle.
 */
export const MermaidRenderer: FC<MermaidRendererProps> = ({ content }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    if (!content) return;

    let cancelled = false;
    const renderDiagram = async () => {
      try {
        const mermaid = await import("mermaid");
        mermaid.default.initialize({
          startOnLoad: false,
          theme: "default",
          securityLevel: "strict",
        });

        const id = `mermaid-${Date.now()}`;
        const { svg: rendered } = await mermaid.default.render(id, content);
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to render diagram");
          setSvg(null);
        }
      }
    };

    renderDiagram();
    return () => {
      cancelled = true;
    };
  }, [content]);

  if (!content) {
    return (
      <div className="cuadra-p-4 cuadra-text-sm cuadra-text-[var(--cuadra-text-secondary)]">
        No content
      </div>
    );
  }

  if (error) {
    return (
      <div className="cuadra-flex cuadra-flex-col cuadra-h-full">
        <div className="cuadra-px-4 cuadra-py-2 cuadra-text-xs cuadra-text-red-500 cuadra-bg-red-50 cuadra-border-b cuadra-border-red-200">
          Diagram parse error: {error}
        </div>
        <pre className="cuadra-flex-1 cuadra-overflow-auto cuadra-p-4 cuadra-m-0 cuadra-text-sm cuadra-font-mono cuadra-leading-relaxed">
          <code>{content}</code>
        </pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-h-full cuadra-p-4">
        <div className="cuadra-text-sm cuadra-text-[var(--cuadra-text-secondary)]">
          Rendering diagram...
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-h-full cuadra-p-4 cuadra-overflow-auto cuadra-bg-[var(--cuadra-bg-primary)]"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};
